import {
    Component,
    Inject,
    OnInit,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    FormBuilder,
    Validators,
    ReactiveFormsModule,
    FormGroup,
    FormControl
} from '@angular/forms';
import { finalize } from 'rxjs';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
    MatDialogModule
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { CityService } from '../../plants/services/city.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlantsService } from '../../plants/services/plants.service';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MapDialogComponent, MapResult } from '../../plants/add-plant/map-dialog/map-dialog.component';

export type UpdateCityData = {
    id: number;
    title: string;
    timeZoneId: string;
    latitude: number | null;
    longitude: number | null;
};

type UpdateCityForm = FormGroup<{
    id: FormControl<number | null>;
    title: FormControl<string | null>;
    timeZoneId: FormControl<string | null>;
    latitude: FormControl<number | null>;
    longitude: FormControl<number | null>;
}>;

@Component({
    selector: 'app-update-city',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatOptionModule,
        MatSelectModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslatePipe
    ],
    templateUrl: './update-city.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './update-city.component.scss'
})
export class UpdateCityComponent implements OnInit {
    saving = false;
    timeZones: string[] = [];
    selectedAddress: string | null = null;
    form: UpdateCityForm;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UpdateCityData,
        private ref: MatDialogRef<UpdateCityComponent, UpdateCityData | null>,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private citySvc: CityService,
        private sb: MatSnackBar,
        private plantsSvc: PlantsService,
        private translate: TranslateService
    ) {
        this.form = this.fb.group({
            id: this.fb.control<number | null>(data?.id ?? null, {
                validators: [Validators.required]
            }),
            title: this.fb.control<string | null>(data?.title ?? '', {
                validators: [Validators.required, Validators.minLength(2), Validators.pattern(/\S/)]
            }),
            timeZoneId: this.fb.control<string | null>(data?.timeZoneId ?? '', {
                validators: [Validators.required]
            }),
            latitude: this.fb.control<number | null>(data?.latitude ?? null, {
                validators: [Validators.required, Validators.min(-90), Validators.max(90)]
            }),
            longitude: this.fb.control<number | null>(data?.longitude ?? null, {
                validators: [Validators.required, Validators.min(-180), Validators.max(180)]
            })
        });
    }

    ngOnInit(): void {
        this.plantsSvc.getTimeZones().subscribe({
            next: res => {
                this.timeZones = (res?.timeZones ?? [])
                    .map((z: any) => z.title)
                    .sort();
            },
            error: () => {
                this.timeZones = [];
            }
        });
    }

    close(): void {
        if (this.saving) return;
        this.ref.close(null);
    }

    openMap(): void {
        if (this.saving) return;

        const latitude = this.form.controls.latitude.value;
        const longitude = this.form.controls.longitude.value;
        const initial = this.hasValidCoordinates(latitude, longitude)
            ? {
                lat: Number(latitude),
                lng: Number(longitude),
                displayName: this.selectedAddress ?? undefined
            }
            : undefined;

        const mapRef = this.dialog.open(MapDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            maxHeight: '94vh',
            autoFocus: false,
            restoreFocus: true,
            data: { initial }
        });

        mapRef.afterClosed().subscribe((result: MapResult | undefined) => {
            if (!result) return;

            this.form.patchValue({
                latitude: this.roundCoordinate(result.lat),
                longitude: this.roundCoordinate(result.lng)
            });

            this.selectedAddress = result.displayName;
            this.form.controls.latitude.markAsDirty();
            this.form.controls.longitude.markAsDirty();
            this.form.controls.latitude.updateValueAndValidity();
            this.form.controls.longitude.updateValueAndValidity();
            this.form.updateValueAndValidity();
        });
    }

    clearSelectedAddress(): void {
        this.selectedAddress = null;
    }

    private hasValidCoordinates(latitude: unknown, longitude: unknown): boolean {
        const lat = Number(latitude);
        const lng = Number(longitude);

        return Number.isFinite(lat) && Number.isFinite(lng)
            && lat >= -90 && lat <= 90
            && lng >= -180 && lng <= 180;
    }

    private roundCoordinate(value: number): number {
        return Number(value.toFixed(7));
    }

    onSubmit(): void {
        if (this.saving) return;

        this.form.updateValueAndValidity();

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const { id, title, timeZoneId, latitude, longitude } = this.form.getRawValue();

        const payload = {
            id: Number(id),
            title: String(title ?? '').trim(),
            timeZoneId: String(timeZoneId ?? '').trim(),
            latitude: Number(latitude),
            longitude: Number(longitude)
        };

        this.saving = true;
        this.citySvc
            .Updatecity(payload)
            .pipe(finalize(() => (this.saving = false)))
            .subscribe({
                next: (city: any) => {
                    const updated: UpdateCityData = {
                        id: Number(city?.id ?? payload.id),
                        title: String(city?.title ?? payload.title),
                        timeZoneId: String(city?.timeZoneId ?? payload.timeZoneId),
                        latitude: this.toCoordinate(city?.latitude, payload.latitude),
                        longitude: this.toCoordinate(city?.longitude, payload.longitude)
                    };

                    this.notify(this.translate.instant('City updated successfully.'), 'success');
                    this.ref.close(updated);
                },
                error: err => {
                    this.notify(err?.statusMessage || this.translate.instant('City update failed.'), 'error');
                }
            });
    }

    getLatitudeError(): string {
        const control = this.form.controls.latitude;
        if (control.hasError('required')) return 'Latitude is required';
        if (control.hasError('min') || control.hasError('max')) {
            return 'Latitude must be between -90 and 90';
        }
        return '';
    }

    getLongitudeError(): string {
        const control = this.form.controls.longitude;
        if (control.hasError('required')) return 'Longitude is required';
        if (control.hasError('min') || control.hasError('max')) {
            return 'Longitude must be between -180 and 180';
        }
        return '';
    }

    private toCoordinate(value: unknown, fallback: number): number {
        if (value === null || value === undefined || value === '') return fallback;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 3500
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                ? 'snack-error'
                : type === 'warn'
                ? 'snack-warn'
                : 'snack-info';

        this.sb.open(msg, this.translate.instant('close'), {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }
}
